import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { Box, Grid, ListItemText } from '@material-ui/core';
import { theme } from 'config/theme';
import i18n from 'i18n-js';
import { AnimatedCounter, BasicThreeDotsMenu, StyledChip } from 'components/shared';
import styles from '../../styles';
import { images } from 'assets';
import KPICard from '../../shared/kpiCard';
import DateFilters from '../DateFIlters';
import { DateHistoryData } from '../finances';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import dayjs from 'dayjs';
import financeStore from 'store/financeStore';
import PaymentReportTable from './components/paymentsTable';
import { ChartBarCheckbox, ChartDataItem, Charts } from '../finances/components/Charts';
import { printElement } from '../../shared/printHelper';

interface PaymentsTabProps {
    restaurantId?: string;
    isOld?: boolean;
    style?: React.CSSProperties;
}

function PaymentsTab(props: PaymentsTabProps) {
    const { restaurantId, isOld: isAdminView } = props;
    const classes = styles();
    const dispatch = useDispatch();
    const report = useSelector((state: ApplicationState) => state.finance.paymentReport);
    const currency = useSelector(
        (state: ApplicationState) =>
            state.restaurantDetails?.restaurantInformation?.paymentDetails?.currency || 'CHF'
    );
    const [dateFilter, setDateFilter] = useState(0);
    const [chartView, setChartView] = useState(false);
    const [selectedDatesHistory, setSelectedDatesHistory] = useState<DateHistoryData[]>([]);
    const [hoveredSwitchIcon, setHoveredSwitchIcon] = useState(0);
    const [dateFrom, setDateFrom] = useState<Date | null>(null);
    const [dateTo, setDateTo] = useState<Date | null>(null);
    const paymentMethods = Array.from(new Set(report.transactions.map((x) => x.method)));
    const dataList: ChartDataItem[] = (report.transactions || []).map((x) => ({
        name: x.method,
        amount: x.amount,
        date: x.paymentDate,
    }));
    const containerRef = useRef<HTMLDivElement>(null);
    //const totalPaymentsAmount = report.payments.reduce((acc, next) => (acc += next.amount), 0);
    const totalPaymentsCount = report.payments.reduce(
        (acc, next) => (acc += next.transactionCount),
        0
    );
    const totalRefundsAmount = report.refunds.reduce(
        (acc, next) => (acc += next.amount + next.tipsAmount),
        0
    );

    const cashRefunds = report.refunds.filter((x) => x.method.toUpperCase() === 'CASH');

    function getData(
        dayAgo: number,
        dateHistoryList?: DateHistoryData[] | undefined,
        dateFrom?: Date | undefined,
        dateTo?: Date | undefined
    ): void {
        var from = dateFrom ?? dayjs().subtract(1, 'day').toDate();
        var to = dateTo ?? dayjs().toDate();

        if (!dateFrom && !dateTo) {
            dateTo = new Date();
            dateFrom = dayjs().subtract(dayAgo, 'days').toDate();
        }

        dateHistoryList && setSelectedDatesHistory(dateHistoryList);

        dispatch(
            financeStore.actionCreators.getPaymentReport(
                formatDateForServer(from),
                formatDateForServer(to),
                restaurantId
            )
        );
    }

    function formatDateForServer(date: Date = new Date()) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        const milliseconds = String(date.getMilliseconds()).padStart(3, '0');

        // Format as ISO string but with local time (no timezone conversion)
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
    }

    function getIconSource(i: number) {
        if (hoveredSwitchIcon > 0) {
            if (i === 0 && hoveredSwitchIcon === 1) {
                return images.icons.listSelected;
            }
            if (i === 1 && hoveredSwitchIcon === 2) {
                return images.icons.graphSelected;
            }
        }

        switch (chartView) {
            case true:
                switch (i) {
                    case 0:
                        return images.icons.list;
                    case 1:
                        return images.icons.graphSelected;
                }
                break;
            default: {
                switch (i) {
                    case 0:
                        return images.icons.listSelected;
                    case 1:
                        return images.icons.graph;
                }
            }
        }
    }

    function handleDateRangeChange(from: Date | null, to: Date | null): void {
        setDateFrom(from);
        setDateTo(to);
    }

    function exportData(): void {
        var from = formatDateForServer(dateFrom ?? dayjs().subtract(1, 'day').toDate());
        var to = formatDateForServer(dateTo ?? dayjs().toDate());

        dispatch(financeStore.actionCreators.getPaymentReportExcel(from, to, restaurantId));
    }

    useEffect(() => {
        getData(1);
    }, []);

    function getCheckboxList(): ChartBarCheckbox[] {
        return paymentMethods.map<ChartBarCheckbox>((method) => {
            return {
                id: method,
                name: i18n.t(`finances.paymentMethodEnum.${method.toLowerCase()}`),
                label: <>{i18n.t(`finances.paymentMethodEnum.${method.toLowerCase()}`)}</>,
            };
        });
    }

    function handlePrint(): void {
        printElement(containerRef);
    }

    return (
        <div
            ref={containerRef}
            className={classes.pBottom5}
            style={{ height: '100%', overflow: 'auto' }}
        >
            {!isAdminView && (
                <div className={classes.topContainer} style={{ justifyContent: 'space-between' }}>
                    <StyledChip
                        icon={<InfoOutlinedIcon />}
                        label={i18n.t('finances.filteredByPaymentConfirmationDate')}
                        size="small"
                        labelFontSize={12}
                        labelColor={theme.palette.primary.dark}
                        variant="outlined"
                        color="primary"
                    />
                </div>
            )}

            <div className={classes.kpiContainer}>
                <Box mb={3}>
                    <Grid container spacing={2}>
                        <Grid item xs xl={2}>
                            <KPICard
                                label={i18n.t('finances.netPayments')}
                                value={
                                    <AnimatedCounter
                                        value={report.totalAmount - totalRefundsAmount}
                                        formatFn={(val) => `${currency} ${val.toFixed(2)}`}
                                    />
                                }
                                sub={`${currency} ${report.totalAmount.toFixed(2)} ${i18n
                                    .t('finances.received')
                                    .toLowerCase()} \n${currency} ${totalRefundsAmount.toFixed(
                                    2
                                )} ${i18n.t('finances.refunded').toLowerCase()}`}
                            />
                        </Grid>
                        <Grid item xs xl={2}>
                            <KPICard
                                label={i18n.t('finances.cash')}
                                value={
                                    <AnimatedCounter
                                        value={report.byCash.amount}
                                        formatFn={(val) => `${currency} ${val.toFixed(2)}`}
                                    />
                                }
                                sub={`${report.byCash.transactionCount} ${i18n
                                    .t('finances.payments')
                                    .toLowerCase()}, ${
                                    cashRefunds?.reduce(
                                        (acc, curr) => acc + curr.transactionCount,
                                        0
                                    ) || 0
                                } ${i18n.t('finances.refunds').toLowerCase()}`}
                            />
                        </Grid>
                        <Grid item xs xl={2}>
                            <KPICard
                                label={i18n.t('finances.cardEft')}
                                value={
                                    <AnimatedCounter
                                        value={report.byCard.amount}
                                        formatFn={(val) => `${currency} ${val.toFixed(2)}`}
                                    />
                                }
                                sub={`${report.byCard.transactionCount} ${i18n
                                    .t('finances.payments')
                                    .toLowerCase()}, ${report.refunds
                                    .filter((x) => x.method.toUpperCase() === 'CARD')
                                    .reduce((acc, curr) => acc + curr.transactionCount, 0)} ${i18n
                                    .t('finances.refunds')
                                    .toLowerCase()}`}
                            />
                        </Grid>
                        <Grid item xs xl={2}>
                            <KPICard
                                label={i18n.t('finances.debtor')}
                                value={
                                    <AnimatedCounter
                                        value={report.byDebtor.amount}
                                        formatFn={(val) => `${currency} ${val.toFixed(2)}`}
                                    />
                                }
                                sub={`${report.byDebtor.transactionCount} ${i18n
                                    .t('finances.payments')
                                    .toLowerCase()}, ${
                                    report.refunds.filter(
                                        (x) => x.method.toUpperCase() === 'DEBTOR'
                                    ).length
                                } ${i18n.t('finances.refunds').toLowerCase()}`}
                            />
                        </Grid>
                        <Grid item xs xl={2}>
                            <KPICard
                                label={i18n.t('finances.twint')}
                                value={
                                    <AnimatedCounter
                                        value={report.byTwint.amount}
                                        formatFn={(val) => `${currency} ${val.toFixed(2)}`}
                                    />
                                }
                                sub={`${report.byTwint.transactionCount} ${i18n
                                    .t('finances.payments')
                                    .toLowerCase()}, ${
                                    report.refunds.filter((x) => x.method.toUpperCase() === 'TWINT')
                                        .length
                                } ${i18n.t('finances.refunds').toLowerCase()}`}
                            />
                        </Grid>
                        <Grid item xs xl={2}>
                            <KPICard
                                label={i18n.t('finances.tips')}
                                value={
                                    <AnimatedCounter
                                        value={report.totalTipsAmount}
                                        formatFn={(val) => `${currency} ${val.toFixed(2)}`}
                                    />
                                }
                                sub={i18n.t('finances.includedInTotal')}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </div>

            <div className={classes.dateContainer}>
                <div className={classes.flexRow}>
                    <DateFilters
                        dateFilter={dateFilter}
                        setDateFilter={setDateFilter}
                        getData={getData}
                        selectedDatesHistory={selectedDatesHistory}
                        setSelectedDatesHistory={setSelectedDatesHistory}
                        isChartsShowing={chartView}
                        onDateRangeChange={handleDateRangeChange}
                    />
                    <span style={{ marginLeft: 20 }}></span>
                    <div className={classes.switchGraphContainer}>
                        <img
                            onClick={() => setChartView(false)}
                            onMouseOver={() => setHoveredSwitchIcon(1)}
                            onMouseLeave={() => setHoveredSwitchIcon(0)}
                            className={classes.switchBasic}
                            src={getIconSource(0)}
                            alt="switch-graph"
                        />
                        <img
                            onClick={() => {
                                setChartView(true);
                            }}
                            onMouseOver={() => setHoveredSwitchIcon(2)}
                            onMouseLeave={() => setHoveredSwitchIcon(0)}
                            className={classes.switchBasic}
                            src={getIconSource(1)}
                            alt="switch-gaph"
                        />
                    </div>
                    <BasicThreeDotsMenu
                        id="hide-in-print"
                        items={[
                            <div
                                id="hide-in-print"
                                className={classes.threeDotsMenuItemContainer}
                                onClick={handlePrint}
                            >
                                <img
                                    src={images.icons.print}
                                    alt="print-icon"
                                    className={classes.threeDotsItemIcon}
                                />
                                <ListItemText primary={i18n.t('common.print')} />
                            </div>,
                            <div
                                id="hide-in-print"
                                className={classes.threeDotsMenuItemContainer}
                                onClick={exportData}
                            >
                                <img
                                    src={images.icons.fileDownload}
                                    alt="file-icon"
                                    className={classes.threeDotsItemIcon}
                                />
                                <ListItemText primary={i18n.t('common.excelExport')} />
                            </div>,
                        ]}
                    />
                </div>
            </div>

            {chartView && (
                <Charts
                    currency={currency}
                    selectedDatesHistory={selectedDatesHistory}
                    dateFilter={dateFilter}
                    dataList={dataList}
                    checkboxList={getCheckboxList()}
                    onDateClick={(selectedDates, newDateFilter, dateHistoryList) => {
                        const { startDate, endDate } = selectedDates;
                        setSelectedDatesHistory(dateHistoryList);
                        return dispatch(
                            financeStore.actionCreators.getPaymentReport(
                                formatDateForServer(startDate),
                                formatDateForServer(endDate),
                                restaurantId
                            )
                        );
                    }}
                />
            )}
            {!chartView && (
                <PaymentReportTable
                    payments={report.payments}
                    refunds={report.refunds}
                    currency={currency}
                    totalPaymentsCount={totalPaymentsCount}
                    totalAmount={report.totalAmount}
                    totalTips={report.totalTipsAmount}
                />
            )}
        </div>
    );
}

export default PaymentsTab;
